import { RefObject, useEffect } from 'react';
import type { SelectedOverlayElement } from './useOverlaySelection';

export interface ArrowNudge {
  /** Pointer-equivalent travel in on-screen px: 1, or 10 with Shift. */
  dx: number;
  dy: number;
}

export interface UseArrowNudgeArgs {
  enabled: boolean;
  selected: SelectedOverlayElement | null;
  /** The editor's canvas; keys are ignored while the cursor is in a text box inside it. */
  rootRef: RefObject<HTMLElement | null>;
  onNudge: (selected: SelectedOverlayElement, nudge: ArrowNudge) => void;
}

export const NUDGE_PX = 1;
export const NUDGE_SHIFT_PX = 10;

/**
 * Arrow keys move the selected element: 1 px, Shift for 10 px, measured on
 * the canvas as you see it. The host turns the travel into its own units
 * (a set element's gap or offset, a design element's position). Nothing
 * happens while typing (arrows move the caret) or when an open picker owns
 * the keyboard.
 */
export function useArrowNudge({ enabled, selected, rootRef, onNudge }: UseArrowNudgeArgs): void {
  useEffect(() => {
    if (!enabled || !selected) return;
    const onKey = (e: KeyboardEvent) => {
      const step = e.shiftKey ? NUDGE_SHIFT_PX : NUDGE_PX;
      let dx = 0;
      let dy = 0;
      if (e.key === 'ArrowUp') dy = -step;
      else if (e.key === 'ArrowDown') dy = step;
      else if (e.key === 'ArrowLeft') dx = -step;
      else if (e.key === 'ArrowRight') dx = step;
      else return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae) {
        const tag = ae.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || ae.isContentEditable) return;
      }
      if (document.querySelector('[data-overlay-popover]')) return;
      // Only when the selection lives in this editor (several cards share the page).
      const root = rootRef.current;
      if (root && !root.querySelector('[data-overlay-element][data-selected]')) return;
      e.preventDefault();
      onNudge(selected, { dx, dy });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled, selected, rootRef, onNudge]);
}
